import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MailCheck } from "lucide-react";
import api, { apiError } from "../lib/api";
import { LOGO_URL } from "../lib/accents";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      <div className="w-full max-w-md fade-up">
        <img src={LOGO_URL} alt="UnCloned®" className="h-9 w-auto" />
        {sent ? (
          <div className="mt-10" data-testid="forgot-success">
            <div className="w-14 h-14 rounded-full bg-[#D0FF0D] flex items-center justify-center">
              <MailCheck className="text-[#000000]" size={24} />
            </div>
            <h2 className="font-display font-black text-3xl text-[#000000] mt-6">Check your email</h2>
            <p className="text-gray-500 mt-3 text-sm">
              If an account exists for <span className="font-bold text-[#000000]">{email}</span>, we&rsquo;ve sent a password reset link. It&rsquo;ll expire in 30 minutes.
            </p>
            <p className="text-xs text-gray-400 mt-4">Didn&rsquo;t get it? Check your spam folder, or ask your UnCloned® account manager for help.</p>
            <Link
              to="/login"
              data-testid="back-to-login-link"
              className="inline-flex items-center gap-2 mt-8 text-sm font-bold text-[#DF1283] hover:underline"
            >
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h2 className="font-display font-black text-3xl text-[#000000] mt-10">Forgot your password?</h2>
            <p className="text-gray-500 mt-2 text-sm">
              No worries — enter the email tied to your account and we&rsquo;ll send you a link to set a new password.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Email</label>
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2] focus:border-transparent"
                />
              </div>
              <button
                data-testid="forgot-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-[#000000] text-white rounded-full px-6 py-3 font-bold text-sm tracking-[0.08em] hover:bg-[#DF1283] transition-colors duration-200 disabled:opacity-60"
              >
                {loading ? "SENDING..." : "SEND RESET LINK"}
              </button>
            </form>
            <Link
              to="/login"
              data-testid="back-to-login-link"
              className="inline-flex items-center gap-2 mt-6 text-sm font-bold text-gray-500 hover:text-[#DF1283] transition-colors"
            >
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
